/**
 * `Docs_Registry.ts` — port of Unity's `AI/Registry/PDF_Registry.cs`, the
 * **Knowledge** tool family over the project's document library.
 *
 * File name deviation: Unity calls the file `PDF_Registry` because the family
 * grew out of its in-app PDF viewer, but only one of the four tools is
 * PDF-specific and the other two search documents of any type. `Docs_` matches
 * what the tools actually do and the web folder that backs them
 * (`models/docs/`).
 *
 * `Save_PDF_placement` is NOT registered: it persists the placement of Unity's
 * floating in-app PDF panel, which the web does not have (documents open in a
 * browser tab). Registering it would let the model promise something that
 * cannot happen.
 *
 * Descriptions / `humanDescription` / `labelWhenActive` are verbatim from
 * `PDF_Registry.cs:14-50` for the first three tools — they are half of the
 * prompt surface, and the main agent prompt is written against this exact
 * wording.
 *
 * The last three are web-first and STUBBED (`previewNotImplemented.ts`): the
 * Answers-from-Docs spec wants a cited document to open on the passage that
 * answered the question, then step through further hits, and be searchable by
 * voice while open. That needs an in-app viewer the web does not have. Their
 * descriptions state the intended behaviour, not the stub, so the agent
 * reaches for them and the user hears one clear "not yet" instead of a
 * confident answer about a document nobody opened.
 */

import { AgentToolNames } from "./agentToolNames";
import { type AIAgentTool, buildParams, stringParam } from "./AIAgentToolRegistryModel";

export function addDocsTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.PromptSearchDocs,
      labelWhenActive: "Searching docs...",
      category: "Knowledge",
      description:
        `Semantic search over project documents via the context engine's knowledge query path (vector store). `
        + `Returns relevant info for the prompt and preserves explicit numeric values and original document units. `
        + `Use for factual/content questions (including about a part/component) before pulling broad scene context; `
        + `if the user explicitly names a scene target, resolve that name per the system prompt first. `
        + `NOT for finding files by name (use ${AgentToolNames.SearchDocByFileName}). `
        + `For a known/selected part needing part-level notes, use ${AgentToolNames.GetPartContext}. `
        + `Citations attach automatically - the answer and its citations arrive TOGETHER, produced by this call, so an answer you did not search for reaches the user with nothing under it. `
        + `CALL AGAIN whenever your answer would state a different figure than the one you just gave, including a follow-up that narrows the same subject to one product or model. A different figure is a different fact from a different document: the earlier result cites the earlier answer, not this one, and only a fresh search can match the right document and attribute it. `
        + `SILENT CALL - this search takes seconds and that wait must not be filled: say nothing before it ("let me check", "one moment"), and speak only once the result is in.`,
      humanDescription:
        "Search project documents by meaning to answer questions or find relevant information.",
      parameters: buildParams({
        prompt: stringParam(
          "Question/request to answer by searching project documents.",
          false,
        ),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.SearchDocByFileName,
      labelWhenActive: "Searching doc files...",
      category: "Knowledge",
      description:
        `Direct filename string search over project Library documents (no semantic search). `
        + `Returns matching documents as citations. Use to find/list/confirm documents by name `
        + `(e.g. 'manual.pdf' or short query 'Assembly A manual'). `
        + `ALSO LISTS THE WHOLE LIBRARY: call it with a bare request like 'all documents' when the `
        + `user asks what documents exist, what you have access to, or to see everything - it then `
        + `returns every document with a citation each, and that list is the answer. You have no `
        + `other way to know what the Library holds, so never answer that question from memory and `
        + `never say you cannot: call this first. `
        + `For document content answers use ${AgentToolNames.PromptSearchDocs}.`,
      humanDescription: "Find project documents by filename, or list everything in the Library.",
      parameters: buildParams({
        prompt: stringParam(
          "Filename or short filename query to search the Library, or a bare request such as "
            + "'all documents' to list every document.",
          false,
        ),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.FindAndShowPDF,
      labelWhenActive: "Finding PDF...",
      category: "Knowledge",
      description:
        "Finds a PDF in the project Library by filename/search terms and SHOWS it as a source "
        + "reference under your reply. It does NOT open the document — the user taps that reference "
        + "to open it. Use when the user asks to show/open a specific PDF. "
        + "Provide the filename if known (e.g. 'manual.pdf') or a short query (e.g. 'Assembly A manual'). "
        + "If multiple match, the tool returns an error with candidates so you can ask the user which one they want.",
      humanDescription: "Show a PDF from project documents as a reference to tap.",
      parameters: buildParams({
        prompt: stringParam("PDF filename (e.g. 'manual.pdf') or search query.", false),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.OpenDocAtReference,
      labelWhenActive: "Opening reference...",
      category: "Knowledge",
      description:
        "Opens a document the answer cited and lands on the first passage that answers the question, highlighted. "
        + `Use when an answer came from a document and the user wants to SEE it - 'show me where', 'open that spec', or picking one of the references offered. `
        + `Use ${AgentToolNames.FindAndShowPDF} instead when the user just wants a document open and there is no passage to land on. `
        + "Only one document is open at a time; opening another replaces it.",
      humanDescription: "Open a cited document at the highlighted passage that answers the question.",
      parameters: buildParams({
        fileName: stringParam("File name of the cited document, e.g. 'ergo-spec.pdf'.", false),
        quote: stringParam(
          "The passage to land on, quoted from the answer as closely as you can.",
          false,
        ),
      }),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GoToNextDocResult,
      labelWhenActive: "Next result...",
      category: "Knowledge",
      description:
        "Moves to the next match inside the document that is currently open. "
        + "Use for 'next', 'next one', 'show me the next result'. Scoped to the open document - there is no stepping between documents.",
      humanDescription: "Go to the next match in the open document.",
      parameters: buildParams(),
      enabled: false,
      writerOnly: false,
    },
    {
      name: AgentToolNames.SearchInOpenDoc,
      labelWhenActive: "Searching the open doc...",
      category: "Knowledge",
      description:
        "Searches inside the document that is already open and highlights the first match. "
        + `Use when the user asks about something 'in this doc' while one is open; ${AgentToolNames.GoToNextDocResult} then steps through the rest. `
        + `For a question across the whole library use ${AgentToolNames.PromptSearchDocs}.`,
      humanDescription: "Search the open document and highlight the first match.",
      parameters: buildParams({
        query: stringParam("What to look for in the open document.", false),
      }),
      enabled: false,
      writerOnly: false,
    },
  );
}
